import React from "react";
import { useFormikContext, FieldArray } from "formik";
import { formatDate } from "utils/misc";
import supplyOptions from "./supply-options";
import { OrderFormContext } from "./provider";
import useTranslate from "hooks/use-translate-language";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import { CheckboxInput } from "components/form";
import { useMediaQuery } from "@mui/material";
import "./styles.scss";

const ChooseSupplies = () => {
  const isSmallScreen = useMediaQuery("(max-width: 900px)");
  const translate = useTranslate();
  const { patientHCPCHistory, supply_options } =
    React.useContext(OrderFormContext);

  const { values, errors } = useFormikContext();

  const [ineligibleSuppliesDisclaimer, setIneligibleSuppliesDisclaimer] =
    React.useState();

  const getCheckbox = (selectedEquipment, thisItem, value, text, itemId, descId, eligibilityId) => {
    return (
      <CheckboxInput
        value={value}
        name={text}
        checked={values.requested_equipment.includes(value)}
        onChange={e => {
          if (
            thisItem == null ||
            thisItem.isEligible ||
            thisItem.isSoonEligible
          ) {
            if (e.target.checked) selectedEquipment.push(value);
            else {
              const idx = values.requested_equipment.indexOf(value);
              selectedEquipment.remove(idx);
            }
          } else {
            if (!thisItem.isEligible && !thisItem.isSoonEligible) {
              if (e.target.checked) {
                setIneligibleSuppliesDisclaimer(value);
              } else {
                const idx = values.requested_equipment.indexOf(value);
                selectedEquipment.remove(idx);
              }
            }
          }
        }}
        color="secondary"
        inputProps={{
          "aria-labelledby": `${itemId} ${descId} ${eligibilityId}`,
        }}
      />
    );
  };

  const getStatusBadge = (classes, thisItem) => {
    return (
      <span
        className={classes}
        style={
          thisItem.isEligible
            ? {
                backgroundColor: "#005C1F",
                color: "white"
              }
            : {
                backgroundColor: "#AA0C24",
                color: "white"
              }
        }
      >
        {thisItem.isEligible
          ? "Eligible"
          : formatDate(thisItem.eligibilityDate, "MM/DD/YYYY")}
      </span>
    );
  };

  return (
    <FieldArray
      name="requested_equipment"
      render={selectedEquipment => (
        <div style={{ paddingBottom: 20 }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              className="eligbleDisclaimer"
              style={{
                color: "#000"
              }}
            >
              {translate("chooseAnItem")} {isSmallScreen}
              <br />
              {translate("suppliesDisclaimer")}
            </div>
            <ul
              style={{
                marginRight: "5%",
                marginLeft: "5%",
                padding: 0,
                listStyle: "none"
              }}
            >
              {!isSmallScreen && (
                <>
                  {supplyOptions
                    .filter(x => supply_options?.includes(x.value))
                    .map(({ value, text, description }) => {
                      const thisItem = patientHCPCHistory?.find(phh =>
                        phh.productType
                          .toLowerCase()
                          ?.includes(value.toLowerCase())
                      );
                      const itemId = `supply-item-${value}`.replace(" ", "-");
                      const descId = `supply-desc-${value}`.replace(" ", "-");
                      const eligibilityId = `supply-eligibility-${value}`.replace(" ", "-");
                      const eligibilityText = thisItem?.isEligible
                        ? "Eligible"
                        : `Eligible on ${formatDate(thisItem?.eligibilityDate, "MM/DD/YYYY")}`;

                      return (
                        <li
                          key={value}
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-evenly",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            padding: 15,
                            alignItems: "center"
                          }}
                        >
                          <div style={{ width: "15%" }}>
                            {getCheckbox(
                              selectedEquipment,
                              thisItem,
                              value,
                              text,
                              itemId,
                              descId,
                              eligibilityId
                            )}
                          </div>
                          <div style={{ width: "25%" }}>
                            <span id={itemId}>{text}</span>
                          </div>
                          <div style={{ width: "35%" }}>
                            <span id={descId}>{description}</span>
                          </div>
                          <div style={{ width: "25%" }}>
                            <span id={eligibilityId} className="sr-only">{eligibilityText}</span>
                            {getStatusBadge("status-info", thisItem)}
                          </div>
                        </li>
                      );
                    })}
                </>
              )}
              {isSmallScreen &&
                supplyOptions
                  .filter(x => supply_options?.includes(x.value))
                  .map(({ value, text, description }) => {
                    const thisItem = patientHCPCHistory?.find(phh =>
                      phh.productType
                        .toLowerCase()
                        ?.includes(value.toLowerCase())
                    );
                    const itemId = `supply-item-${value}`;
                    const descId = `supply-desc-${value}`;
                    const eligibilityId = `supply-eligibility-${value}`;
                    const eligibilityText = thisItem?.isEligible
                      ? "Eligible"
                      : `Eligible on ${formatDate(thisItem?.eligibilityDate, "MM/DD/YYYY")}`;

                    return (
                      <li key={value} className="mobile-card">
                        <div className="card-header">
                          {getCheckbox(
                            selectedEquipment,
                            thisItem,
                            value,
                            text,
                            itemId,
                            descId,
                            eligibilityId
                          )}
                          <div className="card-data">
                            <div>
                              <span id={itemId}>{text}</span>
                            </div>
                            <div>
                              <span id={descId}>{description}</span>
                            </div>
                            <div>
                              <span id={eligibilityId} className="sr-only">{eligibilityText}</span>
                              {getStatusBadge(
                                "status-info status-info-mobile",
                                thisItem
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
            </ul>
          </div>
          {errors.requested_equipment && (
            <div
              id="requested-equipment-error"
              role="alert"
              aria-live="assertive"
              className="order-form-warning"
              style={{
                padding: "10px",
                border: "1px solid #ff3333",
                color: "#ff3333",
                background: "#ffcccc33",
                fontWeight: "600",
                margin: "5px 0"
              }}
            >
              {errors.requested_equipment}
            </div>
          )}

          {ineligibleSuppliesDisclaimer && (
            <Dialog
              open={open}
              onClose={() => {
                setIneligibleSuppliesDisclaimer();
              }}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {translate("inelgibleSuppliesDisclaimer")}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setIneligibleSuppliesDisclaimer();
                  }}
                  color="primary"
                >
                  No
                </Button>
                <Button
                  onClick={() => {
                    selectedEquipment.push(ineligibleSuppliesDisclaimer);
                    setIneligibleSuppliesDisclaimer();
                  }}
                  color="primary"
                >
                  Yes
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </div>
      )}
    />
  );
};

export default ChooseSupplies;
